import React, { useEffect, useState, useCallback } from "react";
import { Plugs, PlugsConnected, Gear } from "@phosphor-icons/react";
import {
    listAgents,
    agentInfoFrom,
    revokeAgent,
} from "@/lib/agent";
import { authLogout, UNAUTHORIZED_EVENT } from "@/lib/api";
import ConnectAgentModal from "@/components/editor/ConnectAgentModal";
import PrinterSelect from "@/components/PrinterSelect";
import { useT } from "@/lib/i18n";

function ago(iso) {
    if (!iso) return "nunca";
    const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
    if (s < 60) return `hace ${s} s`;
    if (s < 3600) return `hace ${Math.round(s / 60)} min`;
    if (s < 86400) return `hace ${Math.round(s / 3600)} h`;
    return `hace ${Math.round(s / 86400)} d`;
}

/**
 * Pastilla de la barra superior: ¿hay un agente de impresión conectado?
 * Clic abre el panel: impresora, conectar otro Mac, revocar, cerrar sesión.
 */
export default function AgentStatusBadge({ onStatusChange }) {
    const t = useT();
    const [status, setStatus] = useState("unknown"); // unknown | connected | offline | unpaired
    const [agents, setAgents] = useState([]);
    const [open, setOpen] = useState(false);
    const [connectOpen, setConnectOpen] = useState(false);

    const check = useCallback(async () => {
        try {
            const list = await listAgents();
            const online = list.find((a) => a.online);
            setAgents(list);
            if (online) {
                setStatus("connected");
                onStatusChange?.({ status: "connected", info: agentInfoFrom(online), agents: list });
            } else {
                setStatus(list.length ? "offline" : "unpaired");
                onStatusChange?.({ status: "offline", info: null, agents: list });
            }
        } catch {
            setStatus("offline");
            onStatusChange?.({ status: "offline", info: null, agents: [] });
        }
    }, [onStatusChange]);

    useEffect(() => {
        check();
        const id = setInterval(check, 10000);
        return () => clearInterval(id);
    }, [check]);

    const online = agents.find((a) => a.online) || null;

    const dotCls = status === "connected" ? "bg-green-500" : status === "unknown" ? "bg-amber-400" : "bg-brand-300";
    const labelText =
        status === "connected"
            ? "Agente conectado"
            : status === "offline"
              ? "Agente desconectado"
              : status === "unpaired"
                ? "Sin agente"
                : "Buscando…";

    async function handleRevoke(agent) {
        // eslint-disable-next-line no-alert
        if (!window.confirm(`¿Revocar el agente «${agent.name}»? Ese Mac dejará de poder imprimir hasta que lo vuelvas a emparejar.`)) return;
        try {
            await revokeAgent(agent.id);
        } finally {
            check();
        }
    }

    async function handleLogout() {
        try {
            await authLogout();
        } finally {
            setOpen(false);
            window.dispatchEvent(new Event(UNAUTHORIZED_EVENT));
        }
    }

    return (
        <div className="relative">
            <button
                data-testid="agent-status-btn"
                onClick={() => setOpen((o) => !o)}
                className="flex items-center gap-2 px-2.5 py-1.5 text-xs bg-white border border-brand-300 text-brand-900 hover:bg-brand-100 font-medium"
                title="Estado del agente de impresión"
            >
                <span className={`w-2 h-2 rounded-full ${dotCls}`}></span>
                <span className="font-mono">{labelText}</span>
                <Gear size={12} className="text-brand-700 ml-0.5" />
            </button>

            {open && (
                <div className="absolute right-0 top-full mt-1.5 w-96 bg-white border border-brand-300 shadow-xl z-50 p-4">
                    <div className="flex items-center gap-2 mb-3">
                        {status === "connected" ? (
                            <PlugsConnected size={18} className="text-green-600" weight="duotone" />
                        ) : (
                            <Plugs size={18} className="text-brand-700" weight="duotone" />
                        )}
                        <h4 className="font-headings font-semibold text-brand-900">Agente de impresión</h4>
                    </div>

                    {status === "connected" && online && (
                        <div className="space-y-2.5 text-sm">
                            <div
                                data-testid="agent-connected-box"
                                className="bg-green-50 border border-green-200 px-3 py-2 text-green-900 font-mono text-xs"
                            >
                                {`✓ «${online.name}» conectado · v${online.version}`}
                            </div>
                            <div>
                                <PrinterSelect agents={agents} testId="agent-printer-select" />
                                <p className="mt-1 text-[11px] text-brand-700">
                                    {t("printer.hint")}
                                </p>
                            </div>
                        </div>
                    )}

                    {status === "offline" && (
                        <div data-testid="agent-offline-box" className="bg-amber-50 border border-amber-200 px-3 py-2 text-amber-900 text-xs space-y-1.5">
                            <p>
                                {`El agente está desconectado (visto ${ago(agents[0]?.lastSeenAt)}). Revisa que esté corriendo en tu Mac:`}
                            </p>
                            <pre className="bg-brand-950 text-brand-100 p-2 font-mono text-[11px] overflow-auto">
                                python3 ~/.zebralab/zebralab_agent.py status
                            </pre>
                        </div>
                    )}

                    {status === "unpaired" && (
                        <div data-testid="agent-unpaired-box" className="bg-amber-50 border border-amber-200 px-3 py-2 text-amber-900 text-xs">
                            Aún no hay ningún Mac conectado. Para imprimir directo desde la web, empareja el agente (una sola vez).
                        </div>
                    )}

                    <div className="mt-3 flex items-center gap-2">
                        <button
                            data-testid="agent-connect-btn"
                            onClick={() => {
                                setOpen(false);
                                setConnectOpen(true);
                            }}
                            className="px-3 py-1.5 text-xs bg-brand-900 text-white hover:bg-brand-800 font-medium"
                        >
                            {agents.length ? "Conectar otro Mac" : "Conectar agente"}
                        </button>
                        <button
                            data-testid="agent-refresh-btn"
                            onClick={check}
                            className="px-3 py-1.5 text-xs bg-white border border-brand-300 text-brand-900 hover:bg-brand-100 font-medium"
                        >
                            Refrescar
                        </button>
                    </div>

                    {agents.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-brand-200">
                            <label className="text-[10px] font-semibold text-brand-800 uppercase tracking-wider mb-1.5 block">
                                Agentes emparejados
                            </label>
                            <ul className="space-y-1">
                                {agents.map((a) => (
                                    <li key={a.id} className="flex items-center justify-between text-xs text-brand-900">
                                        <span className="flex items-center gap-1.5">
                                            <span className={`w-1.5 h-1.5 rounded-full ${a.online ? "bg-green-500" : "bg-brand-300"}`} />
                                            <span>{a.name}</span> <span className="text-brand-600 font-mono">{`· ${a.platform ? `${a.platform} · ` : ""}${a.online ? "en línea" : ago(a.lastSeenAt)}`}</span>
                                        </span>
                                        <button
                                            data-testid={`agent-revoke-${a.id}`}
                                            onClick={() => handleRevoke(a)}
                                            className="text-red-700 hover:underline"
                                        >
                                            Revocar
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    <div className="mt-3 pt-3 border-t border-brand-200 flex justify-end">
                        <button
                            data-testid="agent-logout-btn"
                            onClick={handleLogout}
                            className="text-xs text-brand-700 hover:text-brand-900 hover:underline"
                        >
                            Cerrar sesión
                        </button>
                    </div>
                </div>
            )}

            <ConnectAgentModal
                open={connectOpen}
                onClose={() => {
                    setConnectOpen(false);
                    check();
                }}
                onConnected={check}
            />
        </div>
    );
}
